import { FC } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress, Tooltip } from '@mui/material';
import { TreeNode } from '@/types/tree';
import { useNodeActions } from '@/hooks/useNodeActions';
import { useTree } from '@/hooks/useTree';
import { useTreeStore } from '@/stores/treeStore';
import { iconMap } from '@/lib/theme/iconMapping';
import { TooltipProvider } from '@/components/tree/MenuTooltips';
import { TreeKeyboardShortcutsMenuFooter } from '@/components/tree/TreeKeyboardShortcutsMenuFooter';

/**
 * Position coordinates for the menu
 */
interface MenuPosition {
  x: number;
  y: number;
}

interface AddOptionsMenuProps {
  /** The node data */
  node: TreeNode;
  /** Position where to show the menu */
  position: MenuPosition | null;
  /** Callback when the menu is closed */
  onClose: () => void;
  /** Callback when an add option is selected */
  onAddOption: (action: string) => void;
}

export const AddOptionsMenu: FC<AddOptionsMenuProps> = ({ node, position, onClose, onAddOption }) => {
  const { addMenuActions } = useNodeActions(node);
  const { getTypedData, getNodeData } = useTree();

  if (!position || addMenuActions.length === 0) {
    return null;
  }

  const handleMenuItemClick = (actionId: string) => {
    onAddOption(actionId);
    onClose();
  };

  // Subscribe to loading state for better performance
  const isLoading = (actionId: string) => useTreeStore.getState().loading.get(actionId) || false;

  return (
    <Menu
      open={true}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={{
        top: position.y,
        left: position.x,
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      {addMenuActions.map((action) => {
        const isItemLoading = isLoading(action.id);
        const tooltipText = TooltipProvider.getTooltip(action.id as any, node.type, true, isItemLoading);
        const isDisabled = action.isDisabled?.(node, getTypedData, getNodeData) || false;

        return (
          <Tooltip key={action.id} title={tooltipText} placement="right" arrow>
            <MenuItem onClick={() => handleMenuItemClick(action.id)} disabled={isItemLoading || isDisabled}>
              <ListItemIcon>
                {isItemLoading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : iconMap[action.icon]}
              </ListItemIcon>
              <ListItemText>{action.label}</ListItemText>
            </MenuItem>
          </Tooltip>
        );
      })}
      <TreeKeyboardShortcutsMenuFooter />
    </Menu>
  );
};
